import { ref, watch, computed } from "@vue/composition-api";
import store from "@/store";
import { title } from "@core/utils/filter";

// Notification
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default function useUsersList() {
  // Use toast
  const toast = useToast();

  const refUserListTable = ref(null);

  // Table Handlers
  const tableColumns = [
    { key: "saleId", label: "Order", sortable: true },
    { key: "sales_created", label: "Date", sortable: true },
    { key: "Office_name", label: "Office", sortable: true },
    { key: "services_name", label: "Service", sortable: true },
    { key: "product_price", label: "price", sortable: true },
    { key: "product_qty", label: "qty", sortable: true },
    { key: "sub_total", label: "Subtotal", sortable: true },
    { key: "employeeId", label: "Employee", sortable: true },
    { key: "actions" },
  ];
  const perPage = ref(10);
  const totalUsers = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref("");
  const sortBy = ref("id");
  const isSortDirDesc = ref(true);
  const roleFilter = ref(null);
  const statusFilter = ref(null);
  const itemsOffices = ref([]);
  const itemsStatus = ref([]);
  const rangeDateFilter = ref(null);
  const totalPrice = ref(null);

  // FilterOffice
  const officeFilter = ref(null);
  const officeData = ref([]);

  // FilterCategories
  const categoryFilter = ref(null);
  const categoryData = ref([]);

  // FilterServices
  const serviceFilter = ref(null);
  const serviceData = ref([]);

  const dataMeta = computed(() => {
    const localItemsCount = refUserListTable.value
      ? refUserListTable.value.localItems.length
      : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalUsers.value,
    };
  });

  const refetchData = () => {
    refUserListTable.value.refresh();
  };

  watch(
    [
      currentPage,
      perPage,
      searchQuery,
      roleFilter,
      statusFilter,
      rangeDateFilter,
      officeFilter,
      categoryFilter,
      serviceFilter,
    ],
    () => {
      refetchData();
    }
  );

  const fetchUsers = (ctx, callback) => {
    const { officeEmployee_officeId, rolId, dataUser, Employeess_id } =
      JSON.parse(localStorage.getItem("userData")).dataUser;

    store
      .dispatch("app-user/fetchOrderService", {
        q: searchQuery.value.toLowerCase(),
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        officeId: officeFilter.value || officeEmployee_officeId,
        categoryId: categoryFilter.value || undefined,
        serviceId: serviceFilter.value || statusFilter.value,
        rangeDate: rangeDateFilter.value,
        rolId: rolId,
        employeeId: rolId === 4 ? Employeess_id : null,
      })
      .then((response) => {
        const { data, total, offices: listOffice, categories, services: listServices } = response.data;
        callback(data);
        totalUsers.value = total;
        const arrayOffice = [];
        const arrayData = [];
        const setObj = new Set();
        const setObjOffice = new Set();
        if (total > 0) {
          data.map((data, item) => {
            if (!setObjOffice.has(data.Office_name)) {
              setObjOffice.add(data.Office_name, data);
              arrayOffice.push({
                label: data.Office_name,
                value: data.sales_officeId,
              });
            }

            if (!setObj.has(data.services_name)) {
              setObj.add(data.services_name, data);
              arrayData.push({
                label: data.services_name,
                value: data.serviceId,
                id: item,
              });
            }
          });
          totalPrice.value = data[0].total;

          itemsOffices.value = arrayOffice;
          itemsStatus.value = arrayData;
        } else {
          totalPrice.value = 0;
        }

        // Office Filters
        listOffice.map((data) => {
          if (!officeData.value) {
            officeData.value = [];
          }
          if (rolId && rolId == 3) {
            if (!officeData.value.find((item) => item.id === data.id)) {
              officeData.value.push({
                label: data.name,
                value: data.id,
                id: data.id,
              });
            }
          } else {
            if (!officeData.value.find((item) => item.id === data.id) && data.id == officeEmployee_officeId) {
              officeData.value.push({
                label: data.name,
                value: data.id,
                id: data.id,
              });
            }
          }
        });
        if (!officeFilter || !officeFilter.value) {
          officeFilter.value = officeData.value[0].id;
        }

        // Clear Category Filters
        categoryData.value = [];

        // Category Filters
        categories.map((data) => {
          if (!categoryData.value) {
            categoryData.value = [];
          }
          if (!categoryData.value.find((item) => item.id === data.id)) {
            categoryData.value.push({
              label: data.label,
              value: data.value,
              id: data.id,
            });
          }
        });
        if (!categoryFilter || !categoryFilter.value) {
          categoryFilter.value = null;
        }

        // Clear Product Filters
        serviceData.value = [];

        // Product Filters
        listServices.map((data) => {
          if (!serviceData.value) {
            serviceData.value = [];
          }
          if (!serviceData.value.find((item) => item.id === data.id)) {
            serviceData.value.push({
              label: data.label,
              value: data.value,
              id: data.id,
            });
          }
        });
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error fetching services list",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  };

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveUserRoleVariant = (role) => {
    if (role === "subscriber") return "primary";
    if (role === "author") return "warning";
    if (role === "maintainer") return "success";
    if (role === "editor") return "info";
    if (role === "admin") return "danger";
    return "primary";
  };

  const resolveUserRoleIcon = (role) => {
    if (role === "subscriber") return "UserIcon";
    if (role === "author") return "SettingsIcon";
    if (role === "maintainer") return "DatabaseIcon";
    if (role === "editor") return "Edit2Icon";
    if (role === "admin") return "ServerIcon";
    return "UserIcon";
  };

  const resolveUserStatusVariant = (status) => {
    if (status === true) return "success";
    if (status === false) return "warning";
    return "success";
  };

  const dataItemOffice = itemsOffices;
  const dataItemStatus = itemsStatus;

  return {
    fetchUsers,
    tableColumns,
    perPage,
    currentPage,
    totalUsers,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refUserListTable,
    totalPrice,

    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolveUserStatusVariant,
    refetchData,

    // Extra Filters
    roleFilter,
    statusFilter,
    dataItemStatus,
    dataItemOffice,
    rangeDateFilter,
    // Office Filter
    officeFilter,
    officeData,
    // Category Filter
    categoryFilter,
    categoryData,
    // Product Filter
    serviceFilter,
    serviceData,
  };
}
